import type { PublicError } from '@/types/serializers'

type ServerKind = PublicError['kind']

const SERVER_KIND_SET: Record<ServerKind, true> = {
  not_found: true,
  forbidden: true,
  session_expired: true,
  rate_limited: true,
  server_error: true,
  invalid_request: true,
}

const SERVER_KINDS = Object.keys(SERVER_KIND_SET) as ServerKind[]

export type HttpFailureKind = ServerKind | 'refused' | 'network_error'

export const POLL_REQUEST_TIMEOUT_MS = 5_000

export type FailureClass = 'terminal' | 'rate_limited' | 'transient'

const FAILURE_CLASSES = {
  not_found: 'terminal',
  forbidden: 'terminal',
  session_expired: 'terminal',
  invalid_request: 'terminal',
  rate_limited: 'rate_limited',
  server_error: 'transient',
  refused: 'transient',
  network_error: 'transient',
} satisfies Record<HttpFailureKind, FailureClass>

export function failureClass(kind: HttpFailureKind): FailureClass {
  return FAILURE_CLASSES[kind]
}

export interface HttpSuccess<T> {
  ok: true
  status: number
  data: T
}

export interface HttpFailure {
  ok: false
  status: number
  kind: HttpFailureKind
  error: string | null
  // `unknown` because the body is whatever the network answered; a caller that reads its own refusal shape narrows it.
  body: unknown
}

export type HttpResult<T> = HttpSuccess<T> | HttpFailure

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: FormData | object
  keepalive?: boolean
  signal?: AbortSignal
}

export interface HttpIo {
  cookies: () => string
  fetch: typeof globalThis.fetch
}

const BROWSER: HttpIo = {
  cookies: () => (typeof document === 'undefined' ? '' : document.cookie),
  fetch: (input, init) => fetch(input, init),
}

const XSRF_COOKIE = 'XSRF-TOKEN'

const REDIRECTS: ReadonlyArray<number> = [301, 302, 303, 307, 308]

function cookieValue(cookies: string, name: string): string | null {
  for (const pair of cookies.split(';')) {
    const separator = pair.indexOf('=')
    if (separator === -1 || pair.slice(0, separator).trim() !== name) continue

    const raw = pair.slice(separator + 1).trim()
    try {
      return decodeURIComponent(raw)
    } catch {
      return raw
    }
  }
  return null
}

export function csrfHeaders(cookies: string = BROWSER.cookies()): Record<string, string> {
  const token = cookieValue(cookies, XSRF_COOKIE)
  return token ? { 'X-XSRF-TOKEN': token } : {}
}

// `unknown` because the value is a parsed response body, narrowed field by field.
export function field(body: unknown, key: string): unknown {
  return typeof body === 'object' && body !== null && key in body
    ? (body as Record<string, unknown>)[key]
    : undefined
}

const REFUSED_STATUS = 422

// A gate refusal carries a pt-BR `message`; the archived-agent refusal carries only a code.
export function refusalSentence(failure: HttpFailure): string | null {
  const message = field(failure.body, 'message')
  return failure.status === REFUSED_STATUS && typeof message === 'string' && message !== ''
    ? message
    : null
}

function sentence(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function kindOf(status: number, body: unknown): HttpFailureKind {
  const declared = SERVER_KINDS.find((kind) => kind === field(body, 'kind'))
  if (declared) return declared
  if (status === 404) return 'not_found'
  if (status === 403) return 'forbidden'
  if (status === 429) return 'rate_limited'
  if (status >= 500) return 'server_error'
  return 'refused'
}

function failure(status: number, kind: HttpFailureKind, body: unknown = null): HttpFailure {
  const error = sentence(field(body, 'message')) ?? sentence(field(body, 'error'))
  return { ok: false, status, kind, error, body }
}

async function readBody(response: Response): Promise<{ parsed: boolean; body: unknown }> {
  const text = await response.text().catch(() => '')
  if (text.trim() === '') return { parsed: true, body: null }
  try {
    return { parsed: true, body: JSON.parse(text) }
  } catch {
    return { parsed: false, body: null }
  }
}

function encode(options: RequestOptions): { body?: BodyInit; headers: Record<string, string> } {
  if (options.body === undefined) return { headers: {} }
  if (options.body instanceof FormData) return { body: options.body, headers: {} }
  return {
    body: JSON.stringify(options.body),
    headers: { 'Content-Type': 'application/json' },
  }
}

// Never follows a redirect: the one a JSON call gets here is the sign-in door, a followed 200 of HTML.
export async function requestJson<T = null>(
  url: string,
  options: RequestOptions = {},
  io: HttpIo = BROWSER,
): Promise<HttpResult<T>> {
  const { body, headers } = encode(options)

  let response: Response
  try {
    response = await io.fetch(url, {
      method: options.method ?? 'GET',
      body,
      keepalive: options.keepalive ?? false,
      signal: options.signal,
      redirect: 'manual',
      headers: { Accept: 'application/json', ...csrfHeaders(io.cookies()), ...headers },
    })
  } catch {
    return failure(0, 'network_error')
  }

  if (response.type === 'opaqueredirect' || REDIRECTS.includes(response.status)) {
    return failure(response.status, 'session_expired')
  }

  const read = await readBody(response)
  if (!response.ok) return failure(response.status, kindOf(response.status, read.body), read.body)
  if (!read.parsed) return failure(response.status, 'server_error')

  return { ok: true, status: response.status, data: read.body as T }
}

// The browser caches only a response it was allowed to finish, so the body is read and dropped.
export async function preloadAsset(url: string, io: HttpIo = BROWSER): Promise<boolean> {
  try {
    const response = await io.fetch(url)
    if (!response.ok) return false
    await response.arrayBuffer()
    return true
  } catch {
    return false
  }
}
